export type Role = 'ADMIN' | 'EMPLEADO' | 'PLATFORM_ADMIN';

export interface User {
  id: string;
  username: string;
  email: string;
  fullName: string;
  role: Role;
  isActive?: boolean;
  mustResetPassword?: boolean;
  lastLoginAt?: string | null;
  createdAt?: string;
  branchId?: string | null;
  branch?: { name: string; companyName: string } | null;
}

export interface Category {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  isActive: boolean;
}

export interface Unit {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

export interface Supply {
  id: string;
  code: string;
  name: string;
  categoryId: string;
  unitId: string;
  unitCost: string;
  supplier?: string | null;
  currentStock: string;
  minStock: string;
  isActive: boolean;
  category?: Category;
  unit?: Unit;
}

export type MovementType = 'ENTRADA' | 'SALIDA' | 'AJUSTE';

export interface InventoryMovement {
  id: string;
  supplyId: string;
  type: MovementType;
  quantity: string;
  previousStock: string;
  newStock: string;
  note?: string | null;
  createdAt: string;
  supply?: Supply;
  user?: { id: string; fullName: string; username: string };
}

export interface RecipeComponent {
  id: string;
  recipeId: string;
  supplyId?: string | null;
  componentRecipeId?: string | null;
  quantity: string;
  unitId: string;
  supply?: Supply | null;
  componentRecipe?: Recipe | null;
  unit?: Unit;
}

export interface Recipe {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  imageUrl?: string | null;
  laborCost: string;
  wastePercentage: string;
  isActive: boolean;
  components?: RecipeComponent[];
}

export interface ComponentCostLine {
  componentId: string;
  type: 'INSUMO' | 'RECETA';
  refId: string;
  name: string;
  code: string | null;
  quantity: string;
  unitName: string;
  unitCost: string;
  subtotal: string;
  subComponents?: ComponentCostLine[];
}

export interface RecipeCostResult {
  recipeId: string;
  code: string;
  name: string;
  materialsCost: string;
  wastePercentage: string;
  wasteCost: string;
  laborCost: string;
  productionCost: string;
  components: ComponentCostLine[];
}

export interface CostCalculation {
  id: string;
  recipeId?: string | null;
  recipeCodeSnapshot: string;
  recipeNameSnapshot: string;
  quantity: string;
  materialsCost: string;
  wastePercentage: string;
  wasteCost: string;
  laborCost: string;
  unitProductionCost: string;
  totalProductionCost: string;
  inventoryConsumed: boolean;
  notes?: string | null;
  createdAt: string;
  user?: { id: string; fullName: string; username: string };
  componentLines?: { id: string; description: string; quantity: string; unitCostSnapshot: string; subtotal: string }[];
}

export interface QuoteItem {
  id: string;
  recipeId?: string | null;
  codeSnapshot: string;
  nameSnapshot: string;
  description?: string | null;
  quantity: string;
  unitCost: string;
  totalCost: string;
}

export interface QuoteFormat {
  logoData: string | null;
  footerText: string | null;
  branchName: string;
  companyName: string;
}

export interface Quote {
  id: string;
  code: string;
  clientName: string;
  clientDocument?: string | null;
  clientAddress?: string | null;
  clientPhone?: string | null;
  clientEmail?: string | null;
  items?: QuoteItem[];
  date: string;
  recipeId?: string | null;
  recipeNameSnapshot: string;
  quantity: string;
  unitProductionCost: string;
  totalCost: string;
  notes?: string | null;
  createdAt: string;
  user?: { id: string; fullName: string; username: string };
}

export interface SaleItem {
  id: string;
  recipeId?: string | null;
  recipeCodeSnapshot: string;
  recipeNameSnapshot: string;
  description?: string | null;
  quantity: string;
  unitCost: string;
  totalCost: string;
}

export type SalePaymentMethod = 'EFECTIVO' | 'TARJETA' | 'TRANSFERENCIA';

export const salePaymentMethodLabels: Record<SalePaymentMethod, string> = {
  EFECTIVO: 'Efectivo',
  TARJETA: 'Tarjeta',
  TRANSFERENCIA: 'Transferencia',
};

export interface Sale {
  id: string;
  clientDocument: string;
  clientName: string;
  paymentMethod: SalePaymentMethod;
  totalCost: string;
  createdAt: string;
  items: SaleItem[];
  user?: { id: string; fullName: string; username: string };
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface DashboardData {
  supplyCount: number;
  recipeCount: number;
  lowStockCount: number;
  lowStockSupplies: { id: string; name: string; code: string; currentStock: string; minStock: string; unit: { name: string } }[];
  activeRecipes: { id: string; name: string; imageUrl?: string | null; productionCost: string }[];
  recentCalculations: CostCalculation[];
  recentMovements: InventoryMovement[];
  activeUserCount?: number;
  quoteCount?: number;
}

export interface BackupFile {
  fileName: string;
  sizeBytes: number;
  createdAt: string;
}

export interface Company {
  id: string;
  commercialName: string;
  legalName?: string | null;
  taxId?: string | null;
  country?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  notes?: string | null;
  isActive: boolean;
  _count?: { branches: number };
}

export type LicensePeriod = 'MENSUAL' | 'TRIMESTRAL' | 'SEMESTRAL' | 'ANUAL';
export type PaymentStatus = 'PENDIENTE' | 'PARCIAL' | 'PAGADO' | 'ANULADO';
export type BranchAccessStatus = 'PENDIENTE' | 'ACTIVA' | 'VENCIDA' | 'SUSPENDIDA' | 'INACTIVA';

export interface BranchSubscription {
  id: string;
  branchId: string;
  period: LicensePeriod;
  startDate: string;
  endDate: string;
  contractedAmount: string;
  notes?: string | null;
  payments?: Payment[];
}

export interface Payment {
  id: string;
  branchId: string;
  subscriptionId: string;
  paymentDate: string;
  period: string;
  modality: LicensePeriod;
  contractedAmount: string;
  amountPaid: string;
  paymentMethod: string;
  status: PaymentStatus;
  observations?: string | null;
}

export interface Branch {
  id: string;
  companyId: string;
  name: string;
  commercialName?: string | null;
  address?: string | null;
  city?: string | null;
  department?: string | null;
  country?: string | null;
  phone?: string | null;
  email?: string | null;
  managerName?: string | null;
  managerTitle?: string | null;
  managerPhone?: string | null;
  managerEmail?: string | null;
  isActive: boolean;
  manualAccessEnabled: boolean;
  maxUsers: number;
  observations?: string | null;
  company: Pick<Company, 'id' | 'commercialName' | 'isActive'>;
  accessStatus?: BranchAccessStatus;
  paidAmount?: string;
  currentSubscription?: BranchSubscription | null;
  subscriptions?: BranchSubscription[];
  users?: User[];
  _count?: { users: number };
}

export interface PlatformDashboardData {
  companyCount: number;
  branchCount: number;
  activeBranchCount: number;
  expiredBranchCount: number;
  suspendedBranchCount: number;
  upcomingExpiryCount: number;
  pendingPaymentCount: number;
  pendingPaymentAmount: string;
  receivedPaymentAmount: string;
}
